import React, { useRef, useState, useEffect } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { Button } from './Button';

interface ImageUploadProps {
  currentImage?: string;
  onImageChange: (imageUrl: string) => void;
  /** Si se pasa, no se envía base64 sino el File; el padre sube por multipart. */
  onFileSelect?: (file: File | null) => void;
  label?: string;
  /** Si es true, acepta también PDF además de imágenes (contrato, identificación). */
  acceptPdf?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  onImageChange,
  onFileSelect,
  label = 'Imagen de Portada',
  acceptPdf = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const pendingFileRef = useRef<File | null>(null);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!pendingFileRef.current) {
      setPreview(currentImage || null);
      setPreviewIsPdf(!!(currentImage && currentImage.startsWith('data:application/pdf')));
    }
  }, [currentImage]);

  const processFile = (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !(acceptPdf && isPdf)) {
      alert(acceptPdf ? 'Selecciona una imagen (JPG, PNG) o un PDF.' : 'Por favor selecciona un archivo de imagen válido');
      return;
    }
    const maxSize = isPdf ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(isPdf ? 'El PDF no debe exceder 10MB.' : 'La imagen no debe exceder 5MB');
      return;
    }
    setPreviewIsPdf(!!isPdf);

    if (onFileSelect) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      objectUrlRef.current = URL.createObjectURL(file);
      pendingFileRef.current = file;
      setPreview(objectUrlRef.current);
      onImageChange('');
      onFileSelect(file);
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreview(result);
        onImageChange(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemove = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    pendingFileRef.current = null;
    setPreview(null);
    setPreviewIsPdf(false);
    onImageChange('');
    onFileSelect?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {preview ? (
        <div className="relative">
          <div
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`relative w-full h-64 rounded-lg overflow-hidden border-2 transition-colors ${
              isDragging
                ? 'border-accent bg-accent/10 border-dashed'
                : 'border-gray-200 border-solid'
            }`}
          >
            {(previewIsPdf || preview.startsWith('data:application/pdf')) ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-600">
                <FileText className="w-16 h-16 text-red-500 mb-2" />
                <p className="text-sm font-medium">Documento PDF</p>
              </div>
            ) : (
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            )}
            {isDragging && (
              <div className="absolute inset-0 bg-accent/20 flex items-center justify-center z-10">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-accent mx-auto mb-2" />
                  <p className="text-sm font-medium text-accent">Suelta la imagen aquí</p>
                </div>
              </div>
            )}
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-20"
              type="button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative w-full h-64 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-accent bg-accent/10'
              : 'border-gray-300 hover:border-accent bg-gray-50'
          }`}
        >
          <Upload className={`w-12 h-12 mb-2 ${isDragging ? 'text-accent' : 'text-gray-400'}`} />
          <p className={`text-sm mb-2 ${isDragging ? 'text-accent font-medium' : 'text-gray-600'}`}>
            {isDragging ? 'Suelta el archivo aquí' : acceptPdf ? 'Haz clic o arrastra una imagen o PDF aquí' : 'Haz clic o arrastra una imagen aquí'}
          </p>
          <p className="text-xs text-gray-500">{acceptPdf ? 'PNG, JPG hasta 5MB · PDF hasta 10MB' : 'PNG, JPG hasta 5MB'}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptPdf ? 'image/*,application/pdf' : 'image/*'}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {preview && (
        <Button
          variant="secondary"
          size="sm"
          icon={Upload}
          onClick={handleClick}
        >
          Cambiar Imagen
        </Button>
      )}
    </div>
  );
};

